import * as React from "react";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Dropdown,
  Input,
  Option,
  Radio,
  RadioGroup,
  Switch,
  makeStyles,
  tokens, mergeClasses
} from "@fluentui/react-components";
import {
  Mail24Regular,
  Chat24Regular,
  MailInbox24Regular,
} from "@fluentui/react-icons";
import anredeList from "../data/anrede.json";
import titelList from "../data/titel.json";
import { FluentTelefonInput } from "./forms/widgets/TelefonEingabe";
import { alleFenster } from "./windowManager/browserFenster/fensterRegister";

/* ---------- styles ---------- */
const useStyles = makeStyles({
  surface: {
    width: "min(1040px, 92vw)",
    maxWidth: "1040px",
  },
  body: {
    padding: "0",
    overflow: "auto",
    maxHeight: "calc(90vh - 88px)",
    display: "block",
  },
  header: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    padding: "16px 24px 12px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    gridColumn: "2/3",
    justifySelf: "center",
    fontWeight: 600,
  },
  stepper: {
    display: "flex",
    gap: "16px",
    padding: "10px 24px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    overflowX: "auto",
  },
  step: {
    minWidth: "90px",
    flex: "0 0 auto",
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  stepActive: { color: tokens.colorBrandForeground1, fontWeight: 600 },
  circle: {
    width: "28px",
    height: "28px",
    borderRadius: "999px",
    margin: "0 auto 6px",
    display: "grid",
    placeItems: "center",
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    fontWeight: 600,
  },
  circleActive: {
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundInverted,
  },
  content: {
    padding: "20px 24px 8px",
    display: "grid",
    gap: "16px",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  field: {
    display: "grid",
    gap: "6px",
  },
  actions: {
    padding: "16px 24px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    justifyContent: "space-between",
  },
});

/* ---------- types ---------- */
type ContactType = "person" | "company";

type Werbe = {
  Email: "ja" | "nein";
  SMS: "ja" | "nein";
  Post: "ja" | "nein";
};

type ContactFormData = {
  kontaktTyp: ContactType;
  datenschutzeinwilligung?: string;
  kundenerstinfo?: string;
  anrede?: string;
  titel?: string;
  name: string;
  vorname?: string;

  telefon?: string;
  mobil?: string;
  email?: string;
  fax?: string;
  erreichbarkeit?: string;

  geburtsdatum?: string;
  geburtsort?: string;
  geburtsname?: string;
  staatsangehoerigkeit?: string;
  familienstand?: string;
  kinder?: number;

  beruf?: string;
  branche?: string;
  taetigkeit?: string;
  arbeitgeber?: string;
  webseite?: string;
  umsatzsteuerId?: string;
  sozialvericherungsnummer?: string;

  quelle?: string;
  vorsteuerabzugsberechtigt?: boolean;
  kundenverantwortlicherFrontOffice?: string;
  kundenverantwortlicherBackOffice?: string;
  werbewiderspruch: Werbe;

  strasse?: string;
  postleitzahl?: string;
  ort?: string;
  vollmachten?: string;
};

const initialData: ContactFormData = {
  kontaktTyp: "person",
  anrede: "",
  titel: "",
  name: "",
  vorname: "",
  telefon: "",
  mobil: "",
  email: "",
  fax: "",
  erreichbarkeit: "",
  geburtsdatum: "",
  geburtsort: "",
  geburtsname: "",
  staatsangehoerigkeit: "",
  familienstand: "",
  kinder: 0,
  beruf: "",
  branche: "",
  taetigkeit: "",
  arbeitgeber: "",
  webseite: "",
  umsatzsteuerId: "",
  sozialvericherungsnummer: "",
  quelle: "",
  vorsteuerabzugsberechtigt: false,
  kundenverantwortlicherFrontOffice: "",
  kundenverantwortlicherBackOffice: "",
  werbewiderspruch: { Email: "nein", SMS: "ja", Post: "ja" },
  strasse: "",
  postleitzahl: "",
  ort: "",
  vollmachten: "",
};

/* ---------- component ---------- */
type Props = {
  open: boolean;
  onClose: () => void;
  onSave?: (data: ContactFormData) => void;
};

const ContactMultiStepForm: React.FC<Props> = ({ open, onClose, onSave }) => {
  const s = useStyles();
  const [step, setStep] = React.useState(0);
  const [form, setForm] = React.useState<ContactFormData>(initialData);

  const steps = React.useMemo(() => {
    const base = [
      { key: "type", title: "Kontaktart" },
      { key: "basic", title: "Basisdaten" },
      { key: "address", title: "Adresse" },
      { key: "comm", title: "Kommunikation" },
    ];
    const personOnly = [
      { key: "birth", title: "Geburtsdaten" },
      { key: "job", title: "Beruf" },
    ];
    const companyOnly = [{ key: "company", title: "Firma" }];
    const tail = [
      { key: "misc", title: "Sonstiges" },
      { key: "summary", title: "Zusammenfassung" },
    ];
    return form.kontaktTyp === "person"
      ? [...base, ...personOnly, ...tail]
      : [...base, ...companyOnly, ...tail];
  }, [form.kontaktTyp]);

  const maxStep = steps.length - 1;
  
    React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.shiftKey) {
        const num = Number(e.key);
        if (num >= 1 && num <= steps.length) {
          setStep(num - 1);
          e.preventDefault();
        }
      }
      if (e.key === "ArrowRight" && step < maxStep) {
        setStep((s) => Math.min(s + 1, maxStep));
        e.preventDefault();
      }
      if (e.key === "ArrowLeft" && step > 0) {
        setStep((s) => Math.max(s - 1, 0));
        e.preventDefault();
      }
    };
    alleFenster.addEventListener("keydown", handleKeyDown);
    return () => alleFenster.removeEventListener("keydown", handleKeyDown);
  }, [steps.length]);

  const setField = <K extends keyof ContactFormData>(
    key: K,
    value: ContactFormData[K]
  ) => setForm((p) => ({ ...p, [key]: value }));

  const next = () => setStep((x) => Math.min(x + 1, maxStep));
  const prev = () => setStep((x) => Math.max(x - 1, 0));

  const reset = () => {
    setStep(0);
    setForm(initialData);
  };

  const submit = () => {
    onSave?.(form);
    reset();
    onClose();
  };

  /* ----- sections ----- */
  const SectionType = (
    <div className={s.content}>
      <div className={s.field}>
        <span>Bitte wählen:</span>
        <RadioGroup
          layout="horizontal"
          value={form.kontaktTyp}
          onChange={(_, data) => setField("kontaktTyp", data.value as ContactType)}
        >
          <Radio value="person" label="Privatperson" />
          <Radio value="company" label="Firma" />
        </RadioGroup>
      </div>
    </div>
  );

  const SectionBasic =
    form.kontaktTyp === "person" ? (
      <div className={mergeClasses(s.content, s.grid2)}>
        <div className={s.field}>
          <label htmlFor="anrede">Anrede</label>
          <Dropdown
            id="anrede"
            value={form.anrede}
            placeholder="Anrede wählen"
            onOptionSelect={(_, d) => setField("anrede", d.optionValue ?? "")}
          >
            {anredeList.map((a: any) => (
              <Option key={a.key} value={a.text}>
                {a.text}
              </Option>
            ))}
          </Dropdown>
        </div>

        <div className={s.field}>
          <label htmlFor="titel">Titel</label>
          <Dropdown
            id="titel"
            value={form.titel}
            placeholder="Titel wählen"
            onOptionSelect={(_, d) => setField("titel", d.optionValue ?? "")}
          >
            {titelList.map((t: any) => (
              <Option key={t.key} value={t.text}>
                {t.text}
              </Option>
            ))}
          </Dropdown>
        </div>

        <div className={s.field}>
          <label htmlFor="name">Name</label>
          <Input
            id="name"
            value={form.name}
            onChange={(_, v) => setField("name", v.value)}
            required
          />
        </div>

        <div className={s.field}>
          <label htmlFor="vorname">Vorname</label>
          <Input
            id="vorname"
            value={form.vorname}
            onChange={(_, v) => setField("vorname", v.value)}
          />
        </div>
      </div>
    ) : (
      <div className={mergeClasses(s.content, s.grid2)}>
        <div className={s.field}>
          <label htmlFor="firma">Firmenname</label>
          <Input
            id="firma"
            value={form.name}
            onChange={(_, v) => setField("name", v.value)}
            required
          />
        </div>
        <div className={s.field}>
          <label htmlFor="ustid">Umsatzsteuer-ID</label>
          <Input
            id="ustid"
            value={form.umsatzsteuerId}
            onChange={(_, v) => setField("umsatzsteuerId", v.value)}
          />
        </div>
      </div>
    );

  const SectionAddress = (
    <div className={mergeClasses(s.content, s.grid2)}>
      <div className={s.field}>
        <label htmlFor="strasse">Straße</label>
        <Input
          id="strasse"
          value={form.strasse}
          onChange={(_, v) => setField("strasse", v.value)}
        />
      </div>
      <div className={s.field}>
        <label htmlFor="plz">Postleitzahl</label>
        <Input
          id="plz"
          value={form.postleitzahl}
          onChange={(_, v) => setField("postleitzahl", v.value)}
        />
      </div>
      <div className={s.field}>
        <label htmlFor="ort">Ort</label>
        <Input id="ort" value={form.ort} onChange={(_, v) => setField("ort", v.value)} />
      </div>
    </div>
  );

  const SectionCommunication = (
    <div className={mergeClasses(s.content, s.grid2)}>
      <div className={s.field}>
        <label htmlFor="telefon">Telefon</label>
        <FluentTelefonInput
          id="telefon"
          value={form.telefon}
          onChange={(v) => setField("telefon", v)}
          geschaeftlich
        />
      </div>
      <div className={s.field}>
        <label htmlFor="mobil">Mobil</label>
        <FluentTelefonInput
          id="mobil"
          value={form.mobil}
          onChange={(v) => setField("mobil", v)}
          geschaeftlich
        />
      </div>
      <div className={s.field}>
        <label htmlFor="fax">Fax</label>
        <FluentTelefonInput id="fax" value={form.fax} onChange={(v) => setField("fax", v)} geschaeftlich zentrale={form.telefon} />
      </div>
      <div className={s.field}>
        <label htmlFor="email">Email</label>
        <Input
          id="email"
          value={form.email}
          onChange={(_, v) => setField("email", v.value)}
        />
      </div>
    </div>
  );

  const SectionBirth =
    form.kontaktTyp === "person" ? (
      <div className={mergeClasses(s.content, s.grid2)}>
        <div className={s.field}>
          <label htmlFor="geburtsdatum">Geburtsdatum</label>
          <Input
            id="geburtsdatum"
            value={form.geburtsdatum}
            onChange={(_, v) => setField("geburtsdatum", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="geburtsort">Geburtsort</label>
          <Input
            id="geburtsort"
            value={form.geburtsort}
            onChange={(_, v) => setField("geburtsort", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="geburtsname">Geburtsname</label>
          <Input
            id="geburtsname"
            value={form.geburtsname}
            onChange={(_, v) => setField("geburtsname", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="staatsang">Staatsangehörigkeit</label>
          <Input
            id="staatsang"
            value={form.staatsangehoerigkeit}
            onChange={(_, v) => setField("staatsangehoerigkeit", v.value)}
          />
        </div>
      </div>
    ) : null;

  const SectionJob =
    form.kontaktTyp === "person" ? (
      <div className={mergeClasses(s.content, s.grid2)}>
        <div className={s.field}>
          <label htmlFor="beruf">Berufsstatus</label>
          <Input id="beruf" value={form.beruf} onChange={(_, v) => setField("beruf", v.value)} />
        </div>
        <div className={s.field}>
          <label htmlFor="branche">Branche</label>
          <Input
            id="branche"
            value={form.branche}
            onChange={(_, v) => setField("branche", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="taetigkeit">Tätigkeit</label>
          <Input
            id="taetigkeit"
            value={form.taetigkeit}
            onChange={(_, v) => setField("taetigkeit", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="arbeitgeber">Arbeitgeber</label>
          <Input
            id="arbeitgeber"
            value={form.arbeitgeber}
            onChange={(_, v) => setField("arbeitgeber", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="webseite">Webseite</label>
          <Input
            id="webseite"
            value={form.webseite}
            onChange={(_, v) => setField("webseite", v.value)}
          />
        </div>
      </div>
    ) : null;

  const SectionCompany =
    form.kontaktTyp === "company" ? (
      <div className={mergeClasses(s.content, s.grid2)}>
        <div className={s.field}>
          <label htmlFor="branche2">Branche</label>
          <Input
            id="branche2"
            value={form.branche}
            onChange={(_, v) => setField("branche", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="webseite2">Webseite</label>
          <Input
            id="webseite2"
            value={form.webseite}
            onChange={(_, v) => setField("webseite", v.value)}
          />
        </div>
        <div className={s.field}>
          <label htmlFor="vorsteuer">Vorsteuerabzugsberechtigt</label>
          <Switch
            checked={!!form.vorsteuerabzugsberechtigt}
            onChange={(_, d) => setField("vorsteuerabzugsberechtigt", d.checked)}
          />
        </div>
      </div>
    ) : null;

  const SectionMisc = (
    <div className={mergeClasses(s.content, s.grid2)}>
      <div className={s.field}>
        <label htmlFor="kfo">Kundenverantwortlicher (Front Office)</label>
        <Input
          id="kfo"
          value={form.kundenverantwortlicherFrontOffice}
          onChange={(_, v) => setField("kundenverantwortlicherFrontOffice", v.value)}
        />
      </div>
      <div className={s.field}>
        <label htmlFor="kbo">Kundenverantwortlicher (Back Office)</label>
        <Input
          id="kbo"
          value={form.kundenverantwortlicherBackOffice}
          onChange={(_, v) => setField("kundenverantwortlicherBackOffice", v.value)}
        />
      </div>
      <div className={s.field}>
        <label htmlFor="quelle">Empfehlungsgeber</label>
        <Input id="quelle" value={form.quelle} onChange={(_, v) => setField("quelle", v.value)} />
      </div>
      <div className={s.field}>
        <label htmlFor="familienstand">Familienstand</label>
        <Input
          id="familienstand"
          value={form.familienstand}
          onChange={(_, v) => setField("familienstand", v.value)}
        />
      </div>
    </div>
  );

  const SectionSummary = (
    <div className={s.content}>
      <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
        {JSON.stringify(form, null, 2)}
      </pre>
    </div>
  );

  const renderStepContent = () => {
    const key = steps[step].key;
    switch (key) {
      case "type":
        return SectionType;
      case "basic":
        return SectionBasic;
      case "address":
        return SectionAddress;
      case "comm":
        return SectionCommunication;
      case "birth":
        return SectionBirth;
      case "job":
        return SectionJob;
      case "company":
        return SectionCompany;
      case "misc":
        return SectionMisc;
      case "summary":
        return SectionSummary;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogSurface className={s.surface} aria-describedby={undefined}>
        {/* custom header to center the title */}
        <div className={s.header}>
          <div />
          <DialogTitle className={s.title}>
            {form.kontaktTyp === "company" ? "Firma anlegen" : "Person "}
          </DialogTitle>
          <div />
        </div>

        {/* stepper */}
        <div className={s.stepper}>
          {steps.map((st, i) => {
            const active = i === step;
            return (
              <button
                key={st.key}
                type="button"
                className={mergeClasses(s.step, active && s.stepActive)}
                aria-current={active ? "step" : undefined}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  outline: "none",
                }}
                onClick={() => setStep(i)}
                tabIndex={0}
              >
                <div className={mergeClasses(s.circle, active && s.circleActive)}>{i + 1}</div>
                <div style={{ fontSize: 12 }}>{st.title}</div>
              </button>
            );
          })}
        </div>
        <DialogBody className={s.body}>{renderStepContent()}</DialogBody>

        <DialogActions className={s.actions}>
          <div>
            {step > 0 && (
              <Button appearance="secondary" onClick={prev}>
                Zurück
              </Button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {step < maxStep && (
              <Button appearance="primary" onClick={next}>
                Weiter
              </Button>
            )}
            {step === maxStep && (
              <Button appearance="primary" onClick={submit}>
                Speichern
              </Button>
            )}
            <Button onClick={() => { reset(); onClose(); }}>Abbrechen</Button>
          </div>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};

export default ContactMultiStepForm;
